import TrackingClient from '../TrackingClient'
import * as Events from '../events/index'
import { EventTracker, EventTrackingMethodName } from './EventTracker'

/**
 * Extract the "properties" from each Event type, creating a corresponding method
 *   for each of the methods on the underlying EventTracker class, but accepting
 *   just the properties instead of the Event (see the comment below for more
 *   context)
 */
type ExternalEventTrackingAPI = {
  [P in EventTrackingMethodName]: (
    properties: Exclude<Parameters<EventTracker[P]>[0]['properties'], undefined>
  ) => void
}

/**
 * The `DecoratingEventTracker` is used as the external API for event tracking for
 *   the tracking library. Rather than require a full instantiated Event, each method
 *   accepts plain JavaScript objects which are converted into Events here before
 *   being passed off to the underlying EventTracker.
 */
export default class DecoratingEventTracker implements ExternalEventTrackingAPI {
  private client: TrackingClient

  private tracker: EventTracker

  public constructor(client: TrackingClient, tracker: EventTracker) {
    this.client = client

    this.tracker = tracker
  }

  public trackArticleEndViewed(properties: Events.ArticleEndViewedProperties) {
    return this.tracker.trackArticleEndViewed(
      new Events.ArticleEndViewedEvent(this.client, properties)
    )
  }

  public trackArticleEntryPointClicked(properties: Events.ArticleEntryPointClickedEventProperties) {
    return this.tracker.trackArticleEntryPointClicked(
      new Events.ArticleEntryPointClickedEvent(this.client, properties)
    )
  }

  public trackArticleHubEntryPointClicked(
    properties: Events.ArticleHubEntryPointClickedEventProperties
  ) {
    return this.tracker.trackArticleHubEntryPointClicked(
      new Events.ArticleHubEntryPointClickedEvent(this.client, properties)
    )
  }

  public trackArticleReadNextClicked(properties: Events.ArticleReadNextClickedEventProperties) {
    return this.tracker.trackArticleReadNextClicked(
      new Events.ArticleReadNextClickedEvent(this.client, properties)
    )
  }

  public trackArticleSocialShareClicked(
    properties: Events.ArticleSocialShareClickedEventProperties
  ) {
    return this.tracker.trackArticleSocialShareClicked(
      new Events.ArticleSocialShareClickedEvent(this.client, properties)
    )
  }

  public trackArticleViewed(properties: Events.ArticleViewedEventProperties) {
    return this.tracker.trackArticleViewed(new Events.ArticleViewedEvent(this.client, properties))
  }

  public trackBrandCategoryModalDismissed() {
    return this.tracker.trackBrandCategoryModalDismissed(
      new Events.BrandCategoryModalDismissedEvent(this.client)
    )
  }

  public trackCartAccepted(properties: Events.CartAcceptedEventProperties) {
    return this.tracker.trackCartAccepted(new Events.CartAcceptedEvent(this.client, properties))
  }

  public trackChartersRedirectVisited() {
    return this.tracker.trackChartersRedirectVisited(
      new Events.ChartersRedirectVisitedEvent(this.client)
    )
  }

  public trackCheckoutStarted(properties: Events.CheckoutStartedEventProperties) {
    return this.tracker.trackCheckoutStarted(
      new Events.CheckoutStartedEvent(this.client, properties)
    )
  }

  public trackCheckoutStepCompleted(properties: Events.CheckoutStepCompletedEventProperties) {
    return this.tracker.trackCheckoutStepCompleted(
      new Events.CheckoutStepCompletedEvent(this.client, properties)
    )
  }

  public trackCheckoutStepViewed(properties: Events.CheckoutStepViewedEventProperties) {
    return this.tracker.trackCheckoutStepViewed(
      new Events.CheckoutStepViewedEvent(this.client, properties)
    )
  }

  public trackClientSessionReset(properties: Events.ClientSessionResetEventProperties) {
    return this.tracker.trackClientSessionReset(
      new Events.ClientSessionResetEvent(this.client, properties)
    )
  }

  public trackConsumerReferralLinkShareButtonClicked(
    properties: Events.ConsumerReferralLinkShareButtonClickedEventProperties
  ) {
    return this.tracker.trackConsumerReferralLinkShareButtonClicked(
      new Events.ConsumerReferralLinkShareButtonClickedEvent(this.client, properties)
    )
  }

  public trackConsumerVisitedFromReferralLink(
    properties: Events.ConsumerVisitedFromReferralLinkEventProperties
  ) {
    return this.tracker.trackConsumerVisitedFromReferralLink(
      new Events.ConsumerVisitedFromReferralLinkEvent(this.client, properties)
    )
  }

  public trackContactCollectionCardSubmitted() {
    return this.tracker.trackContactCollectionCardSubmitted(
      new Events.ContactCollectionCardSubmittedEvent(this.client)
    )
  }

  public trackConversationStarterClicked(
    properties: Events.ConversationStarterClickedEventProperties
  ) {
    return this.tracker.trackConversationStarterClicked(
      new Events.ConversationStarterClickedEvent(this.client, properties)
    )
  }

  public trackCuratedItemRemoved(properties: Events.CuratedItemRemovedEventProperties) {
    return this.tracker.trackCuratedItemRemoved(
      new Events.CuratedItemRemovedEvent(this.client, properties)
    )
  }

  public trackCuratedListExpertNoteHelpful(
    properties: Events.CuratedListExpertNoteHelpfulEventProperties
  ) {
    return this.tracker.trackCuratedListExpertNoteHelpful(
      new Events.CuratedListExpertNoteHelpfulEvent(this.client, properties)
    )
  }

  public trackCuratedListExpertNoteNotHelpful(
    properties: Events.CuratedListExpertNoteNotHelpfulEventProperties
  ) {
    return this.tracker.trackCuratedListExpertNoteNotHelpful(
      new Events.CuratedListExpertNoteNotHelpfulEvent(this.client, properties)
    )
  }

  public trackCuratedListViewed(properties: Events.CuratedListViewedEventProperties) {
    return this.tracker.trackCuratedListViewed(
      new Events.CuratedListViewedEvent(this.client, properties)
    )
  }

  public trackCuratedStatusBarViewCurationClicked() {
    return this.tracker.trackCuratedStatusBarViewCurationClicked(
      new Events.CuratedStatusBarViewCurationClickedEvent(this.client)
    )
  }

  public trackDesktopFixedBarClicked() {
    return this.tracker.trackDesktopFixedBarClicked(
      new Events.DesktopFixedBarClickedEvent(this.client)
    )
  }

  public trackEngagementChannelDismissed(
    properties: Events.EngagementChannelDismissedEventProperties
  ) {
    return this.tracker.trackEngagementChannelDismissed(
      new Events.EngagementChannelDismissedEvent(this.client, properties)
    )
  }

  public trackEngagementChannelSubmitted(
    properties: Events.EngagementChannelSubmittedEventProperties
  ) {
    return this.tracker.trackEngagementChannelSubmitted(
      new Events.EngagementChannelSubmittedEvent(this.client, properties)
    )
  }

  public trackEngagementChannelViewed(properties: Events.EngagementChannelViewedEventProperties) {
    return this.tracker.trackEngagementChannelViewed(
      new Events.EngagementChannelViewedEvent(this.client, properties)
    )
  }

  public trackEngagementModalDismissed() {
    return this.tracker.trackEngagementModalDismissed(
      new Events.EngagementModalDismissedEvent(this.client)
    )
  }

  public trackEngagementModalSubmitted() {
    return this.tracker.trackEngagementModalSubmitted(
      new Events.EngagementModalSubmittedEvent(this.client)
    )
  }

  public trackExperimentViewed(properties: Events.ExperimentViewedEventProperties) {
    return this.tracker.trackExperimentViewed(
      new Events.ExperimentViewedEvent(this.client, properties)
    )
  }

  public trackExpertAddedProductToCart(properties: Events.ExpertAddedProductToCartEventProperties) {
    return this.tracker.trackExpertAddedProductToCart(
      new Events.ExpertAddedProductToCartEvent(this.client, properties)
    )
  }

  public trackExpertAddedRecommendedItem(
    properties: Events.ExpertAddedRecommendedItemEventProperties
  ) {
    return this.tracker.trackExpertAddedRecommendedItem(
      new Events.ExpertAddedRecommendedItemEvent(this.client, properties)
    )
  }

  public trackExpertApplicationSubmitted(
    properties: Events.ExpertApplicationSubmittedEventProperties
  ) {
    return this.tracker.trackExpertApplicationSubmitted(
      new Events.ExpertApplicationSubmittedEvent(this.client, properties)
    )
  }

  public trackExpertAttemptedToClaimLeadEscalation(
    properties: Events.ExpertAttemptedToClaimLeadEscalationEventProperties
  ) {
    return this.tracker.trackExpertAttemptedToClaimLeadEscalation(
      new Events.ExpertAttemptedToClaimLeadEscalationEvent(this.client, properties)
    )
  }

  public trackExpertCalloutChatClicked() {
    return this.tracker.trackExpertCalloutChatClicked(
      new Events.ExpertCalloutChatClickedEvent(this.client)
    )
  }

  public trackExpertCreatedExperienceCuration(
    properties: Events.ExpertCreatedExperienceCurationEventProperties
  ) {
    return this.tracker.trackExpertCreatedExperienceCuration(
      new Events.ExpertCreatedExperienceCurationEvent(this.client, properties)
    )
  }

  public trackExpertCuratedItemAdded(properties: Events.ExpertCuratedItemAddedEventProperties) {
    return this.tracker.trackExpertCuratedItemAdded(
      new Events.ExpertCuratedItemAddedEvent(this.client, properties)
    )
  }

  public trackExpertCuratedItemVariationSelected(
    properties: Events.ExpertCuratedItemVariationSelectedEventProperties
  ) {
    return this.tracker.trackExpertCuratedItemVariationSelected(
      new Events.ExpertCuratedItemVariationSelectedEvent(this.client, properties)
    )
  }

  public trackExpertCuratedOfferCtaClicked() {
    return this.tracker.trackExpertCuratedOfferCtaClicked(
      new Events.ExpertCuratedOfferCtaClickedEvent(this.client)
    )
  }

  public trackExpertDismissedSuggestedAction(
    properties: Events.ExpertDismissedSuggestedActionEventProperties
  ) {
    return this.tracker.trackExpertDismissedSuggestedAction(
      new Events.ExpertDismissedSuggestedActionEvent(this.client, properties)
    )
  }

  public trackExpertLeadEscalationNotificationFiltered(
    properties: Events.ExpertLeadEscalationNotificationFilteredEventProperties
  ) {
    return this.tracker.trackExpertLeadEscalationNotificationFiltered(
      new Events.ExpertLeadEscalationNotificationFilteredEvent(this.client, properties)
    )
  }

  public trackExpertNoteHelpful(properties: Events.ExpertNoteHelpfulEventProperties) {
    return this.tracker.trackExpertNoteHelpful(
      new Events.ExpertNoteHelpfulEvent(this.client, properties)
    )
  }

  public trackExpertNoteNotHelpful(properties: Events.ExpertNoteNotHelpfulEventProperties) {
    return this.tracker.trackExpertNoteNotHelpful(
      new Events.ExpertNoteNotHelpfulEvent(this.client, properties)
    )
  }

  public trackExpertProfileConversationStarted() {
    return this.tracker.trackExpertProfileConversationStarted(
      new Events.ExpertProfileConversationStartedEvent(this.client)
    )
  }

  public trackExpertProfileSellableClick() {
    return this.tracker.trackExpertProfileSellableClick(
      new Events.ExpertProfileSellableClickEvent(this.client)
    )
  }

  public trackExpertProfileCategoryTableChanged() {
    return this.tracker.trackExpertProfileCategoryTableChanged(
      new Events.ExpertProfileCategoryTableChangedEvent(this.client)
    )
  }

  public trackExpertRatedShiftDifficulty(
    properties: Events.ExpertRatedShiftDifficultyEventProperties
  ) {
    return this.tracker.trackExpertRatedShiftDifficulty(
      new Events.ExpertRatedShiftDifficultyEvent(this.client, properties)
    )
  }

  public trackExpertRatingFlowCompleted(
    properties: Events.ExpertRatingFlowCompletedEventProperties
  ) {
    return this.tracker.trackExpertRatingFlowCompleted(
      new Events.ExpertRatingFlowCompletedEvent(this.client, properties)
    )
  }

  public trackExpertRatingFlowStarted(properties: Events.ExpertRatingFlowStartedEventProperties) {
    return this.tracker.trackExpertRatingFlowStarted(
      new Events.ExpertRatingFlowStartedEvent(this.client, properties)
    )
  }

  public trackExpertRemovedRecommendedItem(
    properties: Events.ExpertRemovedRecommendedItemEventProperties
  ) {
    return this.tracker.trackExpertRemovedRecommendedItem(
      new Events.ExpertRemovedRecommendedItemEvent(this.client, properties)
    )
  }

  public trackExpertStarredLead() {
    return this.tracker.trackExpertStarredLead(new Events.ExpertStarredLeadEvent(this.client))
  }

  public trackExpertUnstarredLead() {
    return this.tracker.trackExpertUnstarredLead(new Events.ExpertUnstarredLeadEvent(this.client))
  }

  public trackFeedbackSubmitted(properties: Events.FeedbackSubmittedEventProperties) {
    return this.tracker.trackFeedbackSubmitted(
      new Events.FeedbackSubmittedEvent(this.client, properties)
    )
  }

  public trackFullPageTakeoverModalDismissed(
    properties: Events.FullPageTakeoverModalDismissedEventProperties
  ) {
    return this.tracker.trackFullPageTakeoverModalDismissed(
      new Events.FullPageTakeoverModalDismissedEvent(this.client, properties)
    )
  }

  public trackFullPageTakeoverModalSubmitted(
    properties: Events.FullPageTakeoverModalSubmittedEventProperties
  ) {
    return this.tracker.trackFullPageTakeoverModalSubmitted(
      new Events.FullPageTakeoverModalSubmittedEvent(this.client, properties)
    )
  }

  public trackHiringPageSubmitted(properties: Events.HiringPageSubmittedEventProperties) {
    return this.tracker.trackHiringPageSubmitted(
      new Events.HiringPageSubmittedEvent(this.client, properties)
    )
  }

  public trackHiringPageViewed(properties: Events.HiringPageViewedEventProperties) {
    return this.tracker.trackHiringPageViewed(
      new Events.HiringPageViewedEvent(this.client, properties)
    )
  }

  public trackGuestCheckoutStarted(properties: Events.GuestCheckoutStartedEventProperties) {
    return this.tracker.trackGuestCheckoutStarted(
      new Events.GuestCheckoutStartedEvent(this.client, properties)
    )
  }

  public trackItineraryPageViewed(properties: Events.ItineraryPageViewedEventProperties) {
    return this.tracker.trackItineraryPageViewed(
      new Events.ItineraryPageViewedEvent(this.client, properties)
    )
  }

  public trackLandingPageSubmitted(properties: Events.LandingPageSubmittedEventProperties) {
    return this.tracker.trackLandingPageSubmitted(
      new Events.LandingPageSubmittedEvent(this.client, properties)
    )
  }

  public trackLandingPageViewed(properties: Events.LandingPageViewedEventProperties) {
    return this.tracker.trackLandingPageViewed(
      new Events.LandingPageViewedEvent(this.client, properties)
    )
  }

  public trackLeadViewed(properties: Events.LeadViewedEventProperties) {
    return this.tracker.trackLeadViewed(new Events.LeadViewedEvent(this.client, properties))
  }

  public trackMobileFixedBarClicked() {
    return this.tracker.trackMobileFixedBarClicked(
      new Events.MobileFixedBarClickedEvent(this.client)
    )
  }

  public trackNoConversationsRequestModalTriggered() {
    return this.tracker.trackNoConversationsRequestModalTriggered(
      new Events.NoConversationsRequestModalTriggeredEvent(this.client)
    )
  }

  public trackOrderCompleted(properties: Events.OrderCompletedEventProperties) {
    return this.tracker.trackOrderCompleted(new Events.OrderCompletedEvent(this.client, properties))
  }

  public trackPageInteracted() {
    return this.tracker.trackPageInteracted(new Events.PageInteractedEvent(this.client))
  }

  public trackPageViewed() {
    return this.tracker.trackPageViewed(new Events.PageViewedEvent(this.client))
  }

  public trackPathClientFlowCompleted(properties: Events.PathClientFlowCompletedEventProperties) {
    return this.tracker.trackPathClientFlowCompleted(
      new Events.PathClientFlowCompletedEvent(this.client, properties)
    )
  }

  public trackPathClientFlowCreated(properties: Events.PathClientFlowCreatedEventProperties) {
    return this.tracker.trackPathClientFlowCreated(
      new Events.PathClientFlowCreatedEvent(this.client, properties)
    )
  }

  public trackPathClientFlowEntityCreated(
    properties: Events.PathClientFlowEntityCreatedEventProperties
  ) {
    return this.tracker.trackPathClientFlowEntityCreated(
      new Events.PathClientFlowEntityCreatedEvent(this.client, properties)
    )
  }

  public trackPathClientFlowNodeSubmitted(
    properties: Events.PathClientFlowNodeSubmittedEventProperties
  ) {
    return this.tracker.trackPathClientFlowNodeSubmitted(
      new Events.PathClientFlowNodeSubmittedEvent(this.client, properties)
    )
  }

  public trackPathClientFlowNodeViewed(properties: Events.PathClientFlowNodeViewedEventProperties) {
    return this.tracker.trackPathClientFlowNodeViewed(
      new Events.PathClientFlowNodeViewedEvent(this.client, properties)
    )
  }

  public trackPathExited(properties: Events.PathExitedEventProperties) {
    return this.tracker.trackPathExited(new Events.PathExitedEvent(this.client, properties))
  }

  public trackPathFlowCreated(properties: Events.PathFlowCreatedEventProperties) {
    return this.tracker.trackPathFlowCreated(
      new Events.PathFlowCreatedEvent(this.client, properties)
    )
  }

  public trackPathPageSubmitted(properties: Events.PathPageSubmittedEventProperties) {
    return this.tracker.trackPathPageSubmitted(
      new Events.PathPageSubmittedEvent(this.client, properties)
    )
  }

  public trackPathPageViewed(properties: Events.PathPageViewedEventProperties) {
    return this.tracker.trackPathPageViewed(new Events.PathPageViewedEvent(this.client, properties))
  }

  public trackPathQuestionSubmitted(properties: Events.PathQuestionSubmittedEventProperties) {
    return this.tracker.trackPathQuestionSubmitted(
      new Events.PathQuestionSubmittedEvent(this.client, properties)
    )
  }

  public trackPathQuestionViewed(properties: Events.PathQuestionViewedEventProperties) {
    return this.tracker.trackPathQuestionViewed(
      new Events.PathQuestionViewedEvent(this.client, properties)
    )
  }

  public trackPaymentCancelled(properties: Events.PaymentCancelledEventProperties) {
    return this.tracker.trackPaymentCancelled(
      new Events.PaymentCancelledEvent(this.client, properties)
    )
  }

  public trackPaymentFailed(properties: Events.PaymentFailedEventProperties) {
    return this.tracker.trackPaymentFailed(new Events.PaymentFailedEvent(this.client, properties))
  }

  public trackPaymentInfoEntered(properties: Events.PaymentInfoEnteredEventProperties) {
    return this.tracker.trackPaymentInfoEntered(
      new Events.PaymentInfoEnteredEvent(this.client, properties)
    )
  }

  public trackPhoneVerificationCodeIssued() {
    return this.tracker.trackPhoneVerificationCodeIssued(
      new Events.PhoneVerificationCodeIssuedEvent(this.client)
    )
  }

  public trackPhoneVerificationCodeConfirmed() {
    return this.tracker.trackPhoneVerificationCodeConfirmed(
      new Events.PhoneVerificationCodeConfirmedEvent(this.client)
    )
  }

  public trackPhoneVerificationCodeErrored() {
    return this.tracker.trackPhoneVerificationCodeErrored(
      new Events.PhoneVerificationCodeErroredEvent(this.client)
    )
  }

  public trackPhoneVerificationContactUpdated() {
    return this.tracker.trackPhoneVerificationContactUpdated(
      new Events.PhoneVerificationContactUpdatedEvent(this.client)
    )
  }

  public trackPriceComparisonsChatWithExpert(
    properties: Events.PriceComparisonsChatWithExpertEventProperties
  ) {
    return this.tracker.trackPriceComparisonsChatWithExpert(
      new Events.PriceComparisonsChatWithExpertEvent(this.client, properties)
    )
  }

  public trackPriceComparisonsCollapsed(
    properties: Events.PriceComparisonsCollapsedEventProperties
  ) {
    return this.tracker.trackPriceComparisonsCollapsed(
      new Events.PriceComparisonsCollapsedEvent(this.client, properties)
    )
  }

  public trackPriceComparisonsExpanded(properties: Events.PriceComparisonsExpandedEventProperties) {
    return this.tracker.trackPriceComparisonsExpanded(
      new Events.PriceComparisonsExpandedEvent(this.client, properties)
    )
  }

  public trackPriceComparisonsViewed(properties: Events.PriceComparisonsViewedEventProperties) {
    return this.tracker.trackPriceComparisonsViewed(
      new Events.PriceComparisonsViewedEvent(this.client, properties)
    )
  }

  public trackProductAddClicked(properties: Events.ProductAddClickedEventProperties) {
    return this.tracker.trackProductAddClicked(
      new Events.ProductAddClickedEvent(this.client, properties)
    )
  }

  public trackProductAdded(properties: Events.ProductAddedEventProperties) {
    return this.tracker.trackProductAdded(new Events.ProductAddedEvent(this.client, properties))
  }

  public trackProductAddedToWishlist(properties: Events.ProductAddedToWishlistEventProperties) {
    return this.tracker.trackProductAddedToWishlist(
      new Events.ProductAddedToWishlistEvent(this.client, properties)
    )
  }

  public trackProductClicked(properties: Events.ProductClickedEventProperties) {
    return this.tracker.trackProductClicked(new Events.ProductClickedEvent(this.client, properties))
  }

  public trackProductDetailsCollapsed(properties: Events.ProductDetailsCollapsedEventProperties) {
    return this.tracker.trackProductDetailsCollapsed(
      new Events.ProductDetailsCollapsedEvent(this.client, properties)
    )
  }
  public trackProductDetailsExpanded(properties: Events.ProductDetailsExpandedEventProperties) {
    return this.tracker.trackProductDetailsExpanded(
      new Events.ProductDetailsExpandedEvent(this.client, properties)
    )
  }

  public trackProductRated(properties: Events.ProductRatedEventProperties) {
    return this.tracker.trackProductRated(new Events.ProductRatedEvent(this.client, properties))
  }

  public trackProductRemoved(properties: Events.ProductRemovedEventProperties) {
    return this.tracker.trackProductRemoved(new Events.ProductRemovedEvent(this.client, properties))
  }

  public trackProductRemovedFromWishlist(
    properties: Events.ProductRemovedFromWishlistEventProperties
  ) {
    return this.tracker.trackProductRemovedFromWishlist(
      new Events.ProductRemovedFromWishlistEvent(this.client, properties)
    )
  }

  public trackProductsSearched(properties: Events.ProductsSearchedEventProperties) {
    return this.tracker.trackProductsSearched(
      new Events.ProductsSearchedEvent(this.client, properties)
    )
  }

  public trackProductsSearchResultsViewed(
    properties: Events.ProductsSearchResultsViewedEventProperties
  ) {
    return this.tracker.trackProductsSearchResultsViewed(
      new Events.ProductsSearchResultsViewedEvent(this.client, properties)
    )
  }

  public trackProductSpecsCollapsed(properties: Events.ProductSpecsCollapsedEventProperties) {
    return this.tracker.trackProductSpecsCollapsed(
      new Events.ProductSpecsCollapsedEvent(this.client, properties)
    )
  }
  public trackProductSpecsExpanded(properties: Events.ProductSpecsExpandedEventProperties) {
    return this.tracker.trackProductSpecsExpanded(
      new Events.ProductSpecsExpandedEvent(this.client, properties)
    )
  }

  public trackProductViewed(properties: Events.ProductViewedEventProperties) {
    return this.tracker.trackProductViewed(new Events.ProductViewedEvent(this.client, properties))
  }

  public trackProductViewEnded(properties: Events.ProductViewEndedEventProperties) {
    return this.tracker.trackProductViewEnded(
      new Events.ProductViewEndedEvent(this.client, properties)
    )
  }

  public trackProfileSetupPageSubmitted(
    properties: Events.ProfileSetupPageSubmittedEventProperties
  ) {
    return this.tracker.trackProfileSetupPageSubmitted(
      new Events.ProfileSetupPageSubmittedEvent(this.client, properties)
    )
  }

  public trackProfileSetupPageViewed(properties: Events.ProfileSetupPageViewedEventProperties) {
    return this.tracker.trackProfileSetupPageViewed(
      new Events.ProfileSetupPageViewedEvent(this.client, properties)
    )
  }

  public trackPushNotificationsDeniedOnLoad() {
    return this.tracker.trackPushNotificationsDeniedOnLoad(
      new Events.PushNotificationsDeniedOnLoadEvent(this.client)
    )
  }

  public trackPushNotificationsAllowedOnLoad() {
    return this.tracker.trackPushNotificationsAllowedOnLoad(
      new Events.PushNotificationsAllowedOnLoadEvent(this.client)
    )
  }

  public trackPushNotificationsAllowed() {
    return this.tracker.trackPushNotificationsAllowed(
      new Events.PushNotificationsAllowedEvent(this.client)
    )
  }

  public trackPushNotificationsDenied() {
    return this.tracker.trackPushNotificationsDenied(
      new Events.PushNotificationsDeniedEvent(this.client)
    )
  }

  public trackQuickReplyUsed() {
    return this.tracker.trackQuickReplyUsed(new Events.QuickReplyUsedEvent(this.client))
  }

  public trackReferralPageEntryPointViewed(
    properties: Events.ReferralPageEntryPointViewedEventProperties
  ) {
    return this.tracker.trackReferralPageEntryPointViewed(
      new Events.ReferralPageEntryPointViewedEvent(this.client, properties)
    )
  }

  public trackReferralPageViewed(properties: Events.ReferralPageViewedEventProperties) {
    return this.tracker.trackReferralPageViewed(
      new Events.ReferralPageViewedEvent(this.client, properties)
    )
  }

  public trackRequestSubmitted(properties: Events.RequestSubmittedEventProperties) {
    return this.tracker.trackRequestSubmitted(
      new Events.RequestSubmittedEvent(this.client, properties)
    )
  }

  public trackSessionAssociated(properties: Events.SessionAssociatedEventProperties) {
    return this.tracker.trackSessionAssociated(
      new Events.SessionAssociatedEvent(this.client, properties)
    )
  }

  public trackShopperApprovedWidgetClicked(
    properties: Events.ShopperApprovedWidgetClickedEventProperties
  ) {
    return this.tracker.trackShopperApprovedWidgetClicked(
      new Events.ShopperApprovedWidgetClickedEvent(this.client, properties)
    )
  }

  public trackSmsModalDismissed() {
    return this.tracker.trackSmsModalDismissed(new Events.SmsModalDismissedEvent(this.client))
  }

  public trackThreeDSecureNotSupported(properties: Events.ThreeDSecureNotSupportedEventProperties) {
    return this.tracker.trackThreeDSecureNotSupported(
      new Events.ThreeDSecureNotSupportedEvent(this.client, properties)
    )
  }

  public trackTopicPageHelpful(properties: Events.TopicPageHelpfulEventProperties) {
    return this.tracker.trackTopicPageHelpful(
      new Events.TopicPageHelpfulEvent(this.client, properties)
    )
  }

  public trackTopicPageNotHelpful(properties: Events.TopicPageNotHelpfulEventProperties) {
    return this.tracker.trackTopicPageNotHelpful(
      new Events.TopicPageNotHelpfulEvent(this.client, properties)
    )
  }

  public trackTopListExpertProfileClicked(
    properties: Events.TopListExpertProfileClickedEventProperties
  ) {
    return this.tracker.trackTopListExpertProfileClicked(
      new Events.TopListExpertProfileClickedEvent(this.client, properties)
    )
  }

  public trackTopListModuleClicked(properties: Events.TopListModuleClickedEventProperties) {
    return this.tracker.trackTopListModuleClicked(
      new Events.TopListModuleClickedEvent(this.client, properties)
    )
  }

  public trackTopListModuleViewed(properties: Events.TopListModuleViewedEventProperties) {
    return this.tracker.trackTopListModuleViewed(
      new Events.TopListModuleViewedEvent(this.client, properties)
    )
  }

  public trackTopListSellableClicked(properties: Events.TopListSellableClickedEventProperties) {
    return this.tracker.trackTopListSellableClicked(
      new Events.TopListSellableClickedEvent(this.client, properties)
    )
  }

  public trackTopListSocialShareClicked(
    properties: Events.TopListSocialShareClickedEventProperties
  ) {
    return this.tracker.trackTopListSocialShareClicked(
      new Events.TopListSocialShareClickedEvent(this.client, properties)
    )
  }

  public trackTopListViewed(properties: Events.TopListViewedEventProperties) {
    return this.tracker.trackTopListViewed(new Events.TopListViewedEvent(this.client, properties))
  }

  public trackUserLoggedIn(properties: Events.UserLoggedInEventProperties) {
    return this.tracker.trackUserLoggedIn(new Events.UserLoggedInEvent(this.client, properties))
  }

  public trackUserProfileCollectionModalSubmitted(
    properties: Events.UserProfileCollectionModalSubmittedEventProperties
  ) {
    return this.tracker.trackUserProfileCollectionModalSubmitted(
      new Events.UserProfileCollectionModalSubmittedEvent(this.client, properties)
    )
  }

  public trackUserProfileCollectionModalTriggered(
    properties: Events.UserProfileCollectionModalTriggeredEventProperties
  ) {
    return this.tracker.trackUserProfileCollectionModalTriggered(
      new Events.UserProfileCollectionModalTriggeredEvent(this.client, properties)
    )
  }

  public trackUserRegistered(properties: Events.UserRegisteredEventProperties) {
    return this.tracker.trackUserRegistered(new Events.UserRegisteredEvent(this.client, properties))
  }

  public trackVideoPlaybackCompleted(properties: Events.VideoPlaybackCompletedEventProperties) {
    return this.tracker.trackVideoPlaybackCompleted(
      new Events.VideoPlaybackCompletedEvent(this.client, properties)
    )
  }

  public trackVideoPlaybackPaused(properties: Events.VideoPlaybackPausedEventProperties) {
    return this.tracker.trackVideoPlaybackPaused(
      new Events.VideoPlaybackPausedEvent(this.client, properties)
    )
  }

  public trackVideoPlaybackStarted(properties: Events.VideoPlaybackStartedEventProperties) {
    return this.tracker.trackVideoPlaybackStarted(
      new Events.VideoPlaybackStartedEvent(this.client, properties)
    )
  }

  public trackVideoReviewCollapsed(properties: Events.VideoReviewCollapsedEventProperties) {
    return this.tracker.trackVideoReviewCollapsed(
      new Events.VideoReviewCollapsedEvent(this.client, properties)
    )
  }
  public trackVideoReviewExpanded(properties: Events.VideoReviewExpandedEventProperties) {
    return this.tracker.trackVideoReviewExpanded(
      new Events.VideoReviewExpandedEvent(this.client, properties)
    )
  }

  public trackViewCurationLtv(properties: Events.ViewCurationLtvEventProperties) {
    return this.tracker.trackViewCurationLtv(
      new Events.ViewCurationLtvEvent(this.client, properties)
    )
  }

  public trackViewExpertProfileClicked(properties: Events.ViewExpertProfileClickedEventProperties) {
    return this.tracker.trackViewExpertProfileClicked(
      new Events.ViewExpertProfileClickedEvent(this.client, properties)
    )
  }
}
